import React, { useState, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import NotificationBell from './NotificationBell';
import { 
  Menu, X, Home, Truck, Wrench, Package, 
  FileText, Users, LogOut, ChevronLeft, ChevronRight, FileBarChart, Clipboard, ShoppingCart, Receipt,
  Shield, Building2, History, Settings, ClipboardList, ClipboardCheck, Fuel
} from 'lucide-react';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();

  // Check if user has view permission for a module
  const hasPermission = (module) => {
    const permissions = user?.permissions || {};
    return permissions[module]?.includes('view');
  };

  // Navigation items with module-based permissions
  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home, module: 'dashboard' },
    { name: 'Maquinaria', href: '/machinery', icon: Truck, module: 'machinery' },
//     { name: 'Órdenes de Trabajo', href: '/work-orders', icon: ClipboardList, module: 'work_orders' },
//     { name: 'Checklist', href: '/checklists', icon: ClipboardCheck, module: 'maintenance' },
    { name: 'Combustible', href: '/fuel', icon: Fuel, module: 'fuel' },
    { name: 'Mantenimiento', href: '/maintenance', icon: Wrench, module: 'maintenance' },
    { name: 'Repuestos', href: '/spare-parts', icon: Package, module: 'spare_parts' },
    { name: 'Partes Diarios', href: '/daily-reports', icon: FileText, module: 'daily_reports' },
    { name: 'Reportes Trabajo', href: '/work-reports', icon: Clipboard, module: 'work_reports' },
    { name: 'Pedidos', href: '/material-requests', icon: ShoppingCart, module: 'material_requests' },
    { name: 'Compras', href: '/purchases', icon: Receipt, module: 'purchases' },
    { name: 'Operadores', href: '/operators', icon: Users, module: 'operators' },
    { name: 'Reportes', href: '/reports', icon: FileBarChart, module: 'reports' },
  ].filter(item => hasPermission(item.module));

  // Admin/Config navigation
  const adminNavigation = [
    { name: 'Usuarios', href: '/users', icon: Users, module: 'users' },
    { name: 'Roles', href: '/roles', icon: Shield, module: 'roles' },
    { name: 'Empresas', href: '/companies', icon: Building2, module: 'companies' },
    { name: 'Auditoría', href: '/audit-logs', icon: History, module: 'audit' },
  ].filter(item => hasPermission(item.module));

  return (
    <div className="min-h-screen bg-slate-50 flex">

      {/* ================= DESKTOP ================= */}
      <aside
        data-testid="sidebar"
        className={`hidden md:flex ${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-slate-800 text-white transition-all duration-300 flex-col shrink-0`}
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700 shrink-0">
          {sidebarOpen ? (
            <div className="flex items-center min-w-0">
              <img
                src="/logo-mndp.jpg"
                alt="MNDP Asociados"
                className="h-10 w-10 mr-2 rounded object-cover shrink-0"
              />
              <h1
                className="text-lg font-bold text-orange-500 truncate"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                MNDP MaqControl
              </h1>
            </div>
          ) : (
            <img
              src="/logo-mndp.jpg"
              alt="MNDP"
              className="h-10 w-10 rounded object-cover mx-auto"
            />
          )}

          <button
            data-testid="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors shrink-0"
          >
            {sidebarOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
          </button>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive =
              location.pathname === item.href ||
              (item.href !== '/' && location.pathname.startsWith(item.href));

            return (
              <Link
                key={item.name}
                to={item.href}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center px-4 py-3 transition-colors ${
                  isActive
                    ? 'bg-orange-500 text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <Icon size={20} className="shrink-0" />
                {sidebarOpen && (
                  <span className="ml-3 font-medium truncate">{item.name}</span>
                )}
              </Link>
            );
          })}

          {adminNavigation.length > 0 && (
            <>
              <div className="my-4 px-4">
                <div className="border-t border-slate-700" />
                {sidebarOpen && (
                  <p className="text-xs text-slate-500 mt-4 mb-2 uppercase tracking-wider">
                    Administración
                  </p>
                )}
              </div>

              {adminNavigation.map((item) => {
                const Icon = item.icon;
                const isActive =
                  location.pathname === item.href ||
                  location.pathname.startsWith(item.href);

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-center px-4 py-3 transition-colors ${
                      isActive
                        ? 'bg-orange-500 text-white'
                        : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    <Icon size={20} className="shrink-0" />
                    {sidebarOpen && (
                      <span className="ml-3 font-medium truncate">{item.name}</span>
                    )}
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        <div className="border-t border-slate-700 p-4 shrink-0">
          {sidebarOpen ? (
            <div>
              <div className="text-sm font-medium truncate" data-testid="user-name">
                {user?.name}
              </div>
              <div className="text-xs text-slate-400 truncate" data-testid="user-role">
                {user?.role_name || user?.role}
              </div>

              {user?.company_name && (
                <div className="text-xs text-slate-500 mt-1 truncate">
                  {user.company_name}
                </div>
              )}

              <button
                data-testid="logout-button"
                onClick={logout}
                className="mt-3 flex items-center text-sm text-slate-300 hover:text-white transition-colors"
              >
                <LogOut size={16} />
                <span className="ml-2">Cerrar Sesión</span>
              </button>
            </div>
          ) : (
            <button
              data-testid="logout-button-collapsed"
              onClick={logout}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors mx-auto block"
            >
              <LogOut size={20} />
            </button>
          )}
        </div>
      </aside>

      {/* ================= NOTIFICATIONS DESKTOP ================= */}
      <div className="hidden md:block fixed top-3 right-4 z-[80]">
        <NotificationBell />
      </div>

      {/* ================= MOBILE TOP BAR ================= */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-16 bg-slate-800 text-white z-50 border-b border-slate-700 shadow-lg">
        <div className="h-full px-4 flex items-center justify-between">

          <div className="flex items-center min-w-0">
            <img
              src="/logo-mndp.jpg"
              alt="MNDP"
              className="h-9 w-9 rounded object-cover mr-2 shrink-0"
            />

            <div className="min-w-0">
              <h1
                className="text-base font-bold text-orange-500 truncate"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                MNDP MaqControl
              </h1>
              <p className="text-[10px] text-slate-400 truncate">
                {user?.name || 'Usuario'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <NotificationBell />

            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="p-2.5 rounded-lg bg-slate-700 hover:bg-slate-600"
              aria-label="Abrir menú"
            >
              <Menu size={21} />
            </button>
          </div>
        </div>
      </header>

      {/* ================= MOBILE DRAWER ================= */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[60]">

          <button
            type="button"
            aria-label="Cerrar menú"
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />

          <aside className="absolute top-0 right-0 bottom-0 w-[86%] max-w-sm bg-slate-800 text-white shadow-2xl flex flex-col">

            <div className="h-16 px-4 border-b border-slate-700 flex items-center justify-between shrink-0">
              <div className="flex items-center min-w-0">
                <img
                  src="/logo-mndp.jpg"
                  alt="MNDP"
                  className="h-9 w-9 rounded object-cover mr-2"
                />
                <div className="min-w-0">
                  <p className="font-bold text-orange-500 truncate">
                    Menú principal
                  </p>
                  <p className="text-[11px] text-slate-400 truncate">
                    {user?.role_name || user?.role || 'Usuario'}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-lg hover:bg-slate-700"
              >
                <X size={21} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto py-3">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive =
                  location.pathname === item.href ||
                  (item.href !== '/' && location.pathname.startsWith(item.href));

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center mx-3 mb-1 px-4 py-3.5 rounded-xl ${
                      isActive
                        ? 'bg-orange-500 text-white'
                        : 'text-slate-200 hover:bg-slate-700'
                    }`}
                  >
                    <Icon size={21} />
                    <span className="ml-3 font-medium">{item.name}</span>
                  </Link>
                );
              })}

              {adminNavigation.length > 0 && (
                <>
                  <div className="px-5 pt-5 pb-2">
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest">
                      Administración
                    </p>
                  </div>

                  {adminNavigation.map((item) => {
                    const Icon = item.icon;
                    const isActive =
                      location.pathname === item.href ||
                      location.pathname.startsWith(item.href);

                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center mx-3 mb-1 px-4 py-3.5 rounded-xl ${
                          isActive
                            ? 'bg-orange-500 text-white'
                            : 'text-slate-200 hover:bg-slate-700'
                        }`}
                      >
                        <Icon size={21} />
                        <span className="ml-3 font-medium">{item.name}</span>
                      </Link>
                    );
                  })}
                </>
              )}
            </nav>

            <div className="border-t border-slate-700 p-4 shrink-0">
              <div className="mb-3">
                <p className="text-sm font-semibold truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate">
                  {user?.role_name || user?.role}
                </p>
              </div>

              <button
                type="button"
                onClick={logout}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-slate-700 text-slate-200 hover:bg-red-600 hover:text-white"
              >
                <LogOut size={18} />
                Cerrar Sesión
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* ================= MAIN CONTENT ================= */}
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <div className="w-full max-w-[1920px] mx-auto pt-20 pb-24 px-3 sm:px-4 md:pt-4 md:pb-8 lg:px-8">
          {children}
        </div>
      </main>

      {/* ================= MOBILE BOTTOM NAV ================= */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 h-[68px] bg-white border-t border-slate-200 shadow-[0_-4px_15px_rgba(0,0,0,0.08)] z-50">
        <div className="h-full grid grid-cols-5 items-center px-1">

          {[
            { name: 'Inicio', href: '/', icon: Home },
            { name: 'Máquinas', href: '/machinery', icon: Truck },
            { name: 'Partes', href: '/daily-reports', icon: FileText },
            { name: 'Pedidos', href: '/material-requests', icon: ShoppingCart }
          ].map((item) => {
            const Icon = item.icon;
            const isActive =
              location.pathname === item.href ||
              (item.href !== '/' && location.pathname.startsWith(item.href));

            return (
              <Link
                key={item.name}
                to={item.href}
                className={`h-full flex flex-col items-center justify-center ${
                  isActive ? 'text-orange-500' : 'text-slate-500'
                }`}
              >
                <div className={`p-1.5 rounded-lg ${isActive ? 'bg-orange-50' : ''}`}>
                  <Icon size={20} />
                </div>
                <span className="text-[10px] font-medium">{item.name}</span>
              </Link>
            );
          })}

          <button
            type="button"
            onClick={() => setMobileMoreOpen(!mobileMoreOpen)}
            className={`h-full flex flex-col items-center justify-center ${
              mobileMoreOpen ? 'text-orange-500' : 'text-slate-500'
            }`}
          >
            <div className={`p-1.5 rounded-lg ${mobileMoreOpen ? 'bg-orange-50' : ''}`}>
              <Settings size={20} />
            </div>
            <span className="text-[10px] font-medium">Más</span>
          </button>
        </div>

        {mobileMoreOpen && (
          <div className="absolute bottom-[68px] left-2 right-2 bg-white border border-slate-200 rounded-2xl shadow-2xl p-3 max-h-[65vh] overflow-y-auto">

            <div className="flex items-center justify-between px-2 pb-2 mb-2 border-b border-slate-100">
              <div>
                <p className="font-bold text-slate-800">Más opciones</p>
                <p className="text-[11px] text-slate-400">
                  Módulos de MAQControl
                </p>
              </div>

              <button
                type="button"
                onClick={() => setMobileMoreOpen(false)}
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">

              {navigation
                .filter(
                  item =>
                    !['/', '/machinery', '/daily-reports', '/material-requests'].includes(item.href)
                )
                .map((item) => {
                  const Icon = item.icon;
                  const isActive =
                    location.pathname === item.href ||
                    location.pathname.startsWith(item.href);

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMoreOpen(false)}
                      className={`flex items-center gap-2 p-3 rounded-xl border ${
                        isActive
                          ? 'border-orange-200 bg-orange-50 text-orange-600'
                          : 'border-slate-100 bg-slate-50 text-slate-700'
                      }`}
                    >
                      <Icon size={18} />
                      <span className="text-xs font-medium">{item.name}</span>
                    </Link>
                  );
                })}

              {adminNavigation.map((item) => {
                const Icon = item.icon;
                const isActive =
                  location.pathname === item.href ||
                  location.pathname.startsWith(item.href);

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMoreOpen(false)}
                    className={`flex items-center gap-2 p-3 rounded-xl border ${
                      isActive
                        ? 'border-orange-200 bg-orange-50 text-orange-600'
                        : 'border-slate-100 bg-slate-50 text-slate-700'
                    }`}
                  >
                    <Icon size={18} />
                    <span className="text-xs font-medium">{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </nav>
    </div>
  )
};

export default Layout;